import { Button, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { BackfillQueryScopeOptions, MigrateAutoScopeOptions } from '@vy/types';
import { type FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useMigrateToAutomaticTax } from '../../../../../../../hooks/requests/customers/use-migrate-to-automatic-tax';
import { useAnrokBackfillTransactions } from '../../../../../../../hooks/requests/integrations/anrok/use-anrok-backfill-transactions';
import { useDialog } from '../../../../../../../hooks/use-dialog';
import { useEffectOnce } from '../../../../../../../hooks/use-effect-once';
import { queryKeys } from '../../../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../../../utils/vy-mutation';
import { ConfigurationSection } from '../../../containers/ConfigurationSection';
import { ConfigurationsContainer } from '../../../containers/ConfigurationsContainer';
import { AnrokConfigurationControl, AnrokConfigurationFormValues } from '../AnrokConfigurationFormSchema';
import { useFetchAnrokSettings } from '../hooks/useFetchAnrokSettings';
import { BackfillAnrokTransactionsDialog } from './components/BackfillAnrokTransactionsDialog';
import { DefaultProductConfigurationSection } from './components/DefaultProductConfigurationSection';
import { DefaultTaxMethodSection } from './components/DefaultTaxMethodSection';
import { MigrateToAutomaticTaxDialog } from './components/MigrateToAutomaticTaxDialog';
import { TokenConfigurationSection } from './components/TokenConfigurationSection';

type AnrokConfigurationFormProps = {
  control: AnrokConfigurationControl;
};

export const AnrokConfigurationForm: FC<AnrokConfigurationFormProps> = ({ control }) => {
  const { configuration } = useFetchAnrokSettings();
  const { reset } = useFormContext<AnrokConfigurationFormValues>();
  const backfillTransactionsMutation = useAnrokBackfillTransactions();
  const migrateToAutomaticTaxMutation = useMigrateToAutomaticTax();
  const queryClient = useQueryClient();
  const {
    isDialogOpen: isMigrationDialogOpen,
    handleOpen: openMigrationDialog,
    handleClose: closeMigrationDialog,
  } = useDialog();
  const {
    isDialogOpen: isBackfillDialogOpen,
    handleOpen: openBackfillDialog,
    handleClose: closeBackfillDialog,
  } = useDialog();
  const [migrationCompleted, setMigrationCompleted] = useState(false);

  useEffectOnce(() => {
    reset(configuration);
  });

  const handleBackfillConfirm = async (scope: BackfillQueryScopeOptions) => {
    await runMutation(
      backfillTransactionsMutation,
      { scope },
      {
        pending: 'Triggering Anrok invoice sync...',
        success: 'Transaction sync started successfully',
        error: 'Failed to trigger Anrok invoice sync',
      },
    );

    await queryClient.invalidateQueries(queryKeys.integrationSettings());
    closeBackfillDialog();
  };

  const handleMigrationConfirm = async (scope: MigrateAutoScopeOptions) => {
    await runMutation(
      migrateToAutomaticTaxMutation,
      { scope },
      {
        pending: 'Migrating customers to automatic tax...',
        success: 'Customers migrated successfully',
        error: 'Failed to migrate customers',
      },
    );

    setMigrationCompleted(true);
    closeMigrationDialog();
  };

  return (
    <FormContainer>
      <Typography variant="h5">Configurations</Typography>
      <ConfigurationsContainer>
        <TokenConfigurationSection control={control} defaultValues={configuration} />
        <DefaultProductConfigurationSection control={control} defaultValues={configuration} />
        <DefaultTaxMethodSection control={control} defaultValues={configuration} />
        <ConfigurationSection title="Sync" iconName="Synchronize">
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Create all approved invoices in this account as transactions in Anrok.
          </Typography>
          <Button
            variant="outlined"
            disabled={backfillTransactionsMutation.isLoading}
            onClick={openBackfillDialog}
          >
            Backfill Historical Transactions
          </Button>
        </ConfigurationSection>
        <ConfigurationSection title="Tax Migration" iconName="Synchronize">
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Migrate customers from manual tax rates to automatic tax calculation.
          </Typography>
          <Button variant="outlined" disabled={migrationCompleted} onClick={openMigrationDialog}>
            Migrate to Automatic Tax
          </Button>
        </ConfigurationSection>
      </ConfigurationsContainer>
      <BackfillAnrokTransactionsDialog
        open={isBackfillDialogOpen}
        onClose={closeBackfillDialog}
        onConfirm={handleBackfillConfirm}
        isLoading={backfillTransactionsMutation.isLoading}
      />
      <MigrateToAutomaticTaxDialog
        open={isMigrationDialogOpen}
        onClose={closeMigrationDialog}
        onConfirm={handleMigrationConfirm}
        isLoading={migrateToAutomaticTaxMutation.isLoading}
      />
    </FormContainer>
  );
};

const FormContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});
